import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, DestroyRef, ElementRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpeechService, VoiceCommand } from 'src/app/services/media/speech.service';
import { RealtimeVoiceService } from 'src/app/services/media/realtime-voice.service';
import { AccountService, SubscriptionType } from 'src/app/services/account/account.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { VoiceCommandErrorDialogComponent } from '../modals/voice-command-error-dialog/voice-command-error-dialog.component';
import device from 'current-device';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { SubscriptionRequiredDialogComponent } from '../modals/subscriptions-dialog/subscription-required-dialog/subscription-required-dialog.component';
import { CommonModule } from '@angular/common';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { ModalRef } from 'src/app/services/modal';

@Component({
  standalone: true,
  selector: 'app-voice-input',
  templateUrl: './voice-input.component.html',
  styleUrls: ['./voice-input.component.scss'],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class VoiceInputComponent implements OnInit {
  // The button drives the OpenAI Realtime copilot; false falls back to the browser speech-recognition voice commands.
  private static readonly REALTIME_VOICE_ENABLED: boolean = true;

  public readonly IsActive = signal<boolean>(false);

  private readonly realtimeVoiceService = inject(RealtimeVoiceService);
  public readonly realtimePhase = this.realtimeVoiceService.phase;
  public readonly isRealtimeActive = computed(() => this.realtimePhase() !== 'idle');

  private readonly destroyRef = inject(DestroyRef);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private errorDialogRef: ModalRef<VoiceCommandErrorDialogComponent> | null = null;

  // The button's colour ring reacts to the loudness of whoever is talking (the microphone, or the
  // assistant while it speaks); the level is written straight to the `--voice-level` CSS variable
  // each frame, outside change detection.
  private audioContext: AudioContext | null = null;
  private inputAnalyser: AnalyserNode | null = null;
  private outputAnalyser: AnalyserNode | null = null;
  private levelBuffer: Uint8Array<ArrayBuffer> | null = null;
  private animationFrameId: number | null = null;
  private smoothedLevel: number = 0;

  constructor(
    public SpeechRecognitionService: SpeechService,
    private cdr: ChangeDetectorRef,
    public accountService: AccountService,
    public settingsService: SettingsService,
    private consoleLoggerService: ConsoleLoggerService,
    public webviewService: WebviewService,
    private stackedModalsService: StackedModalsService) {
    effect(() => this.inputAnalyser = this.connectAnalyser(this.realtimeVoiceService.inputStream(), this.inputAnalyser));
    effect(() => this.outputAnalyser = this.connectAnalyser(this.realtimeVoiceService.outputStream(), this.outputAnalyser));

    effect(() => {
      if (this.isRealtimeActive()) {
        this.startLevelLoop();
      } else {
        this.stopLevelLoop();
      }
    });
  }

  ngOnInit(): void {
    this.SpeechRecognitionService.ObservableActiveVoiceCommands.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((voiceCommands: VoiceCommand[] | null) => {
      if (voiceCommands != null) {
        this.IsActive.set(voiceCommands.indexOf(VoiceCommand.SetBattery) != -1);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.realtimeVoiceService.stop();
      this.stopLevelLoop();
      this.audioContext?.close().catch(() => { });
      this.audioContext = null;
    });
  }

  public ToggleSpeechRecognition(): void {
    if (VoiceInputComponent.REALTIME_VOICE_ENABLED) {
      this.realtimeVoiceService.toggle();
      return;
    }

    if (this.accountService.getUserSubscriptionType() == SubscriptionType.Free) {
      this.stackedModalsService.openModal(SubscriptionRequiredDialogComponent, { data: { feature: "voice-input" }, autoFocus: false });
    }
    else {
      const desktopEdge = device.desktop() && window.navigator.userAgent.indexOf("Edg") > -1;
      if (('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) && !desktopEdge) {
        if (this.IsActive()) {
          this.SpeechRecognitionService.StopRecognition();
        } else {
          const iosNativeApp = this.webviewService.IsGPSTWebview() && device.ios();
          if (iosNativeApp) {
            this.webviewService.startMicrophoneInteraction();
          }
          else {
            this.StartRecognition();
          }
        }
      }
      else {
        this.openVoiceInputErrorDialog();
      }
    }
  }

  private openVoiceInputErrorDialog(): void {
    this.errorDialogRef = this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
      {
        data: {
          errorMsg: "This feature is not supported on this device.",
          notSupported: true
        },
        autoFocus: false
      });

    this.errorDialogRef.afterClosed().subscribe(() => {
      this.errorDialogRef = null;
    });
  }

  private connectAnalyser(stream: MediaStream | null, previous: AnalyserNode | null): AnalyserNode | null {
    previous?.disconnect();
    if (!stream || stream.getAudioTracks().length === 0) {
      return null;
    }
    try {
      this.audioContext ??= new AudioContext();
      this.audioContext.resume().catch(() => { });
      const analyser = this.audioContext.createAnalyser();
      analyser.fftSize = 512;
      // Analysis only: the analyser is never connected to the destination, so nothing is played twice.
      this.audioContext.createMediaStreamSource(stream).connect(analyser);
      return analyser;
    } catch {
      return null;
    }
  }

  private startLevelLoop(): void {
    if (this.animationFrameId !== null) {
      return;
    }
    const tick = () => {
      const analyser = this.realtimePhase() === 'speaking' ? this.outputAnalyser : this.inputAnalyser;
      const target = analyser ? this.readLevel(analyser) : 0;
      // Fast attack, slow release, so the ring jumps with syllables and settles smoothly.
      this.smoothedLevel += (target - this.smoothedLevel) * (target > this.smoothedLevel ? 0.5 : 0.12);
      this.hostRef.nativeElement.style.setProperty('--voice-level', this.smoothedLevel.toFixed(3));
      this.animationFrameId = requestAnimationFrame(tick);
    };
    this.animationFrameId = requestAnimationFrame(tick);
  }

  private stopLevelLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.smoothedLevel = 0;
    this.hostRef.nativeElement.style.setProperty('--voice-level', '0');
  }

  /** RMS of the current waveform mapped to 0..1 (speech sits around 0.3–0.8). */
  private readLevel(analyser: AnalyserNode): number {
    if (!this.levelBuffer || this.levelBuffer.length !== analyser.fftSize) {
      this.levelBuffer = new Uint8Array(analyser.fftSize);
    }
    analyser.getByteTimeDomainData(this.levelBuffer);
    let sum = 0;
    for (const sample of this.levelBuffer) {
      const centered = (sample - 128) / 128;
      sum += centered * centered;
    }
    return Math.min(1, Math.sqrt(sum / this.levelBuffer.length) * 4);
  }

  private StartRecognition(): void {
    this.SpeechRecognitionService.StartRecognition(
      [VoiceCommand.SetBattery, VoiceCommand.SkipWP, VoiceCommand.SkipCS,
      VoiceCommand.StopNavigation, VoiceCommand.SetWeight, VoiceCommand.NavigateToHome,
      VoiceCommand.NavigateToWork, VoiceCommand.ReadEta, VoiceCommand.ReadEte, VoiceCommand.ReadRemainingDist]
    );
  }
}
